/**
 * Which payment rail checkout actually bills, given what the customer picked.
 *
 * This was an inline `useMemo` in Review.jsx and therefore untestable, which
 * is how it silently overrode customers for months. Collista Lewingdon
 * (2026-09-17) picked a deposit plan on a $1,812 Southwest "Choice Preferred"
 * fare and Review billed her Defera Zero ($0 down) instead, four times; she
 * only escaped by dropping to a $1,032 "Basic" fare with no refunds and no
 * changes, because a non-refundable fare is the one thing the override does
 * not touch. Between Aug 1 and Sep 17, 226 carts across 90 users were moved
 * onto Zero this way and exactly one kept its deposit.
 *
 * The rules, in the order they win:
 *
 *   1. Flight + Hotel packages can never be Defera Zero (XFZXIZ, July 2026),
 *      so an explicit `defera_zero` selection is read as `installments` first
 *      and the auto-upgrade below is suppressed.
 *   2. An explicit opt-out (`choseRefundableInstallments`, the "Deposit plan"
 *      card on the booking-options page) is FINAL. It is never upgraded to
 *      Zero and never re-routed to Capital.
 *   3. While the provider is still loading (`null`), refundable installments
 *      stay `installments` — committing early would hit the wrong Stripe API.
 *   4. Capital is the platform's installment rail when configured, and beats
 *      Zero for anyone who did not land on the Zero path.
 *   5. Otherwise a refundable fare on installments is offered as Zero — the
 *      $0-down upsell. This is the rule that must yield to rule 2.
 */

/**
 * True when this customer is on the $0-down Defera Zero path *implicitly* —
 * refundable fare, installments selected, and no deliberate opt-out. Kept
 * separate because Review also uses it to pick fee tiers and to decide
 * whether it is still waiting on the provider.
 */
export function isDeferaZeroRefundableInstallmentsPath({
  paymentMethod,
  isRefundableFare,
  choseRefundableInstallments,
}) {
  return paymentMethod === 'installments'
    && Boolean(isRefundableFare)
    && !choseRefundableInstallments;
}

/**
 * @param {object} input
 * @param {string} input.paymentMethod              What the customer selected.
 * @param {boolean} input.isRefundableFare          Selected fare tests as fully refundable.
 * @param {boolean} input.choseRefundableInstallments  They picked the "Deposit plan" card.
 * @param {'defera_capital'|'slice'|null} input.installmentsCheckoutProvider  null = still loading.
 * @param {boolean} input.isFlightHotelPackage
 * @returns {string} the rail checkout should bill
 */
export function resolveCheckoutPaymentMethod({
  paymentMethod,
  isRefundableFare,
  choseRefundableInstallments,
  installmentsCheckoutProvider,
  isFlightHotelPackage,
}) {
  const pm = isFlightHotelPackage && paymentMethod === 'defera_zero'
    ? 'installments'
    : paymentMethod;

  const onImplicitZeroPath = isDeferaZeroRefundableInstallmentsPath({
    paymentMethod,
    isRefundableFare,
    choseRefundableInstallments,
  });

  if (installmentsCheckoutProvider === 'defera_capital') {
    if (
      !onImplicitZeroPath
      && (pm === 'installments' || pm === 'miles_installments' || pm === 'defera_capital')
    ) {
      return 'defera_capital';
    }
  }

  if (installmentsCheckoutProvider === null) {
    if (choseRefundableInstallments) return 'installments';
    if (pm === 'installments' && isRefundableFare) return 'installments';
    return pm;
  }

  if (choseRefundableInstallments) return 'installments';
  if (pm === 'installments' && isRefundableFare && !isFlightHotelPackage) return 'defera_zero';
  return pm;
}

/**
 * Build the fare-card list with a "pay a deposit instead" twin of `source`
 * inserted at its natural cabin-tier position — the card that sets
 * `choseRefundableInstallments` and so opts the customer out of the $0-down
 * upsell shown at the front of the list.
 *
 * Placement is "last within its own tier": after every card whose cabin tier
 * is at or below the twin's, before anything above it. With a tier-0
 * refundable fare that yields the intended reading order
 *
 *     Defera Zero, Basic, Main, Comfort, Refundable (installments), First
 *
 * which is the founder's spec (2026-09-17). The refundable fare therefore
 * appears twice: once pinned at the front as the Defera Zero upsell, once in
 * the cabin progression as the deposit option. It must NOT be appended after
 * the whole list (where it landed before, past First) and must NOT be pinned
 * beside the Zero card.
 *
 * @param {object[]} options  Fare cards, already sorted for display.
 * @param {object} source     The refundable fare the twin duplicates.
 * @param {(option: object) => number} tierOf  Cabin tier of a card
 *   (-1 basic, 0 economy/main, 1 premium, 2 business, 3 first).
 */
export function withDepositTwin(options, source, tierOf) {
  const list = Array.isArray(options) ? options : [];
  if (!source) return list;
  const twin = { ...source, _paymentVariant: 'installments' };
  if (typeof tierOf !== 'function') return [...list, twin];

  const twinTier = tierOf(source);
  let insertAt = -1;
  list.forEach((option, i) => {
    if (tierOf(option) <= twinTier) insertAt = i;
  });
  if (insertAt < 0) return [twin, ...list];
  return [...list.slice(0, insertAt + 1), twin, ...list.slice(insertAt + 1)];
}

/**
 * withDepositTwin for every refundable fare, not just the cheapest. Each
 * source gets its own "pay a deposit instead" twin at its cabin-tier
 * position, so a pricier refundable fare (Delta One Extra beside Premium
 * Select Extra) can go on the deposit plan too. It also stays bookable when
 * the Defera Zero cards are hidden for trips under two months out (founder,
 * 2026-10-26). Twins go in from the lowest tier up, and each lands after
 * every card at or below its tier, including twins already placed, so
 * same-tier twins keep their source order.
 *
 * @param {object[]} options  Fare cards, already sorted for display.
 * @param {object[]} sources  The refundable fares to twin.
 * @param {(option: object) => number} tierOf  Cabin tier of a card.
 */
export function withDepositTwins(options, sources, tierOf) {
  const list = Array.isArray(options) ? options : [];
  const unique = [...new Set((Array.isArray(sources) ? sources : []).filter(Boolean))];
  const ordered = typeof tierOf === 'function'
    ? unique.map((source, i) => ({ source, i, tier: tierOf(source) }))
      .sort((a, b) => a.tier - b.tier || a.i - b.i)
      .map(({ source }) => source)
    : unique;
  return ordered.reduce((acc, source) => withDepositTwin(acc, source, tierOf), list);
}
